<template>
  <div class="results-page">
    <h2 class="section-title">Tabulky a statistiky</h2>
    
    <!-- Season filter -->
    <div class="mb-4">
      <select v-model="selectedSeason" class="form-select" @change="loadData">
        <option value="">Vyberte ročník</option>
        <option v-for="season in seasons" :key="season.id" :value="season.id">
          {{ season.name }}
        </option>
      </select>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'table' }" @click="activeTab = 'table'" href="#">
          Tabulka základní části
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'playoff' }" @click="activeTab = 'playoff'" href="#">
          Play off - nasazení
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'played' }" @click="activeTab = 'played'" href="#">
          Odehrané zápasy
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'scoring' }" @click="activeTab = 'scoring'" href="#">
          Kanadské bodování
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'goalies' }" @click="activeTab = 'goalies'" href="#">
          Statistiky brankářů
        </a>
      </li>
    </ul>

    <!-- League table -->
    <div v-if="activeTab === 'table'" class="table-responsive">
      <div v-if="loadingTable" class="text-center my-4">Načítání...</div>
      <table v-else class="table table-striped">
        <thead>
          <tr>
            <th>Poz.</th>
            <th>Tým</th>
            <th>Z</th>
            <th>V</th>
            <th>R</th>
            <th>P</th>
            <th>Skóre</th>
            <th>Body</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(team, index) in leagueTable" :key="team.id">
            <td>{{ index + 1 }}</td>
            <td>
              <div class="d-flex align-items-center">
                <img 
                  v-if="team.logo" 
                  :src="getLogoUrl(team.logo)" 
                  :alt="team.name + ' logo'" 
                  class="team-logo me-2"
                />
                <span v-else class="team-logo-placeholder me-2"></span>
                {{ team.name }}
              </div>
            </td>
            <td>{{ team.played }}</td>
            <td>{{ team.won }}</td>
            <td>{{ team.drawn }}</td>
            <td>{{ team.lost }}</td>
            <td>{{ team.goalsFor }}:{{ team.goalsAgainst }}</td>
            <td>{{ team.points }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Played matches -->
    <div v-if="activeTab === 'played'" class="table-responsive">
      <!-- Playoff filter for played matches -->
      <div class="mb-3">
        <div class="btn-group" role="group">
          <input type="radio" class="btn-check" id="regularSeasonPlayed" v-model="isPlayOff" :value="false" @change="loadData">
          <label class="btn btn-outline-primary" for="regularSeasonPlayed">Základní část</label>
          
          <input type="radio" class="btn-check" id="playoffPlayed" v-model="isPlayOff" :value="true" @change="loadData">
          <label class="btn btn-outline-primary" for="playoffPlayed">Playoff</label>
        </div>
      </div>
      <div v-if="loadingPlayed" class="text-center my-4">Načítání...</div>
      <table v-else class="table">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Domácí</th>
            <th>Skóre</th>
            <th>Hosté</th>
            <th>Akce</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="match in playedMatches" :key="match.id">
            <td>{{ formatDate(match.match_date) }}</td>
            <td>
              <div class="d-flex align-items-center">
                <img 
                  v-if="getTeamLogo(match.home_team_id)" 
                  :src="getLogoUrl(getTeamLogo(match.home_team_id))" 
                  :alt="getTeamName(match.home_team_id) + ' logo'" 
                  class="team-logo me-2"
                />
                <span v-else class="team-logo-placeholder me-2"></span>
                {{ getTeamName(match.home_team_id) }}
              </div>
            </td>
            <td>{{ match.home_score }}:{{ match.away_score }}</td>
            <td>
              <div class="d-flex align-items-center">
                <img 
                  v-if="getTeamLogo(match.away_team_id)" 
                  :src="getLogoUrl(getTeamLogo(match.away_team_id))" 
                  :alt="getTeamName(match.away_team_id) + ' logo'" 
                  class="team-logo me-2"
                />
                <span v-else class="team-logo-placeholder me-2"></span>
                {{ getTeamName(match.away_team_id) }}
              </div>
            </td>
            
            <td class="played-match-actions">
              <div class="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  class="btn btn-sm played-action-btn played-action-btn--stats"
                  @click="showMatchDetails(match)"
                >
                  Statistiky utkání
                </button>
                <!-- Not displayed in the table
                <button
                  type="button"
                  class="btn btn-sm played-action-btn played-action-btn--record"
                  @click="showRecordModal(match)"
                >
                  Zobrazit zápis o utkání
                </button>
                -->
              </div>
            </td>
          
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Schedule -->
    <div v-if="activeTab === 'schedule'" class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Domácí</th>
            <th>vs</th>
            <th>Hosté</th>
            <th>Místo konání</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="match in scheduledMatches" :key="match.id">
            <td>{{ formatDate(match.match_date) }}</td>
            <td>{{ getTeamName(match.home_team_id) }}</td>
            <td>vs</td>
            <td>{{ getTeamName(match.away_team_id) }}</td>
            <td>{{ match.venue }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Scoring stats tab -->
    <div v-if="activeTab === 'scoring'" class="table-responsive">
      <!-- Filters for scoring tab -->
      <div class="mb-3">
        <div class="btn-group mb-2" role="group" aria-label="Část sezóny">
          <input type="radio" class="btn-check" id="regularSeasonScoring" v-model="isPlayOff" :value="false" @change="loadScoringStats">
          <label class="btn btn-outline-primary" for="regularSeasonScoring">Základní část</label>
          
          <input type="radio" class="btn-check" id="playoffScoring" v-model="isPlayOff" :value="true" @change="loadScoringStats">
          <label class="btn btn-outline-primary" for="playoffScoring">Playoff</label>
        </div>
        <select v-model="selectedScoringTeam" class="form-select mb-2" @change="loadScoringStats">
          <option value="">Všechny týmy</option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <div class="scoring-top-limit-wrap" aria-label="Počet hráčů v žebříčku">
          <!-- <span class="scoring-top-limit-caption">Žebříček</span> -->
          <div class="scoring-top-limit-group" role="group">
            <input type="radio" class="btn-check" id="scoringTop50" v-model.number="scoringTopLimit" :value="50" @change="loadScoringStats">
            <label class="btn scoring-top-limit-btn" for="scoringTop50">TOP 50</label>
            <input type="radio" class="btn-check" id="scoringTop30" v-model.number="scoringTopLimit" :value="30" @change="loadScoringStats">
            <label class="btn scoring-top-limit-btn" for="scoringTop30">TOP 30</label>
            <input type="radio" class="btn-check" id="scoringTop10" v-model.number="scoringTopLimit" :value="10" @change="loadScoringStats">
            <label class="btn scoring-top-limit-btn" for="scoringTop10">TOP 10</label>
          </div>
        </div>
      </div>
      <div v-if="loadingScoring" class="text-center my-4">Načítání...</div>
      <table v-else class="table">
        <thead>
          <tr>
            <th>Hráč</th>
            <th>Tým</th>
            <th>Góly</th>
            <th>Asistence</th>
            <th>Body</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="player in scoringStats" :key="player.player_id">
            <td>{{ player.first_name }} {{ player.last_name }}</td>
            <td>
              <div class="d-flex align-items-center"> 
                <img 
                  v-if="getTeamLogo(player.team_id)" 
                  :src="getLogoUrl(getTeamLogo(player.team_id))" 
                  :alt="player.team_name + ' logo'" 
                  class="team-logo me-2"
                />
                
                <span v-else class="team-logo-placeholder me-2"></span>
                
                {{ player.team_name }}
              </div>
            </td>
            <td>{{ player.goals }}</td>
            <td>{{ player.assists }}</td>
            <td>{{ player.points }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Playoff bracket tab -->
    <div v-if="activeTab === 'playoff'" class="table-responsive">
      <div v-if="loadingPlayoff" class="text-center my-4">Načítání...</div>
      <div v-else class="playoff-bracket">
        <div v-if="playoffBracket.length === 0" class="text-center my-4">
          <p>Zatím není vytvořen playoff pavouk pro tento ročník.</p>
        </div>
        <div v-else class="playoff-structure">
          <div v-for="round in getUniquePlayoffRounds()" :key="round" class="playoff-round">
            <h5 class="text-center mb-3">{{ getPlayoffRoundLabel(round) }}</h5>
            <div class="playoff-matches">
              <div v-for="match in getPlayoffBracketByRound(round)" :key="match.id" class="playoff-match">
                <div class="match-card">
                  <div class="team-row">
                    <div class="team-info">
                      <img v-if="match.team1_logo" :src="getLogoUrl(match.team1_logo)" class="team-logo-small" />
                      <span v-else class="team-logo-placeholder-small"></span>
                      <span>{{ match.team1_name || 'TBD' }}</span>
                    </div>
                    <div v-if="match.winner_id === match.team1_id" class="winner-badge">✓</div>
                  </div>
                  <div class="team-row">
                    <div class="team-info">
                      <img v-if="match.team2_logo" :src="getLogoUrl(match.team2_logo)" class="team-logo-small" />
                      <span v-else class="team-logo-placeholder-small"></span>
                      <span>{{ match.team2_name || 'TBD' }}</span>
                    </div>
                    <div v-if="match.winner_id === match.team2_id" class="winner-badge">✓</div>
                  </div>
                  <div v-if="match.winner_name" class="match-winner">
                    Vítěz: {{ match.winner_name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Goalie stats tab -->
    <div v-if="activeTab === 'goalies'" class="table-responsive">
      <!-- Filters for goalies tab -->
      <div class="mb-3">
        <div class="btn-group mb-2" role="group">
          <input type="radio" class="btn-check" id="regularSeasonGoalies" v-model="isPlayOff" :value="false" @change="loadGoalieStats">
          <label class="btn btn-outline-primary" for="regularSeasonGoalies">Základní část</label>
          
          <input type="radio" class="btn-check" id="playoffGoalies" v-model="isPlayOff" :value="true" @change="loadGoalieStats">
          <label class="btn btn-outline-primary" for="playoffGoalies">Playoff</label>
        </div>
        <select v-model="selectedGoaliesTeam" class="form-select" @change="loadGoalieStats">
          <option value="">Všechny týmy</option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </div>
      <div v-if="loadingGoalies" class="text-center my-4">Načítání...</div>
      <table v-else class="table">
        <thead>
          <tr>
            <th>Brankář</th>
            <th>Tým</th>
            <th>Střely celkem</th>
            <th>Zákroky</th>
            <th>Obdržené</th>
            <th>Úspěšnost</th>
            <th>GAA</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="goalie in goalieStats" :key="goalie.player_id">
            <td>{{ goalie.first_name }} {{ goalie.last_name }}</td>
            <td>
              <div class="d-flex align-items-center">
                <img 
                  v-if="getTeamLogo(goalie.team_id)" 
                  :src="getLogoUrl(getTeamLogo(goalie.team_id))" 
                  :alt="goalie.team_name + ' logo'" 
                  class="team-logo me-2"
                />
                <span v-else class="team-logo-placeholder me-2"></span>
                {{ goalie.team_name }}
              </div>
            </td>
            <td>{{ goalie.shots_against }}</td>
            <td>{{ goalie.saves }}</td>
            <td>{{ goalie.goals_against }}</td>
            <td>{{ goalie.save_percentage }}%</td>
            <td>{{ goalie.gaa }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Match Details Modal -->
    <div class="modal fade" id="matchDetailsModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <template v-if="selectedMatch">
                {{ getTeamName(selectedMatch.home_team_id) }}
                {{ selectedMatch.home_score }}:{{ selectedMatch.away_score }}
                {{ getTeamName(selectedMatch.away_team_id) }}
              </template>
              <template v-else>Detail zápasu</template>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedMatch">
              <p class="match-detail-meta small mb-3" v-if="selectedMatch.match_date">
                {{ formatDate(selectedMatch.match_date) }}
              </p>
              <h6>Výsledek: {{ selectedMatch.home_score }}:{{ selectedMatch.away_score }}</h6>
              
              <!-- Zápis z utkání (obrázek) 
              <div v-if="selectedMatch.record_image" class="mb-3">
                <h6>Zápis z utkání:</h6>
                <img :src="getLogoUrl(selectedMatch.record_image)" alt="Zápis z utkání" style="max-width:100%;border-radius:8px;box-shadow:0 2px 8px #0a1023;" />
              </div>
              -->

              <!-- Goals (read-only) -->
              <h6 class="mt-4">Góly</h6>
              <div class="table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Tým</th>
                      <th>Střelec</th>
                      <th>Asistence</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="goal in matchGoals" :key="goal.id">
                      <td>{{ getPlayerTeamName(goal.player_id) }}</td>
                      <td>{{ getPlayerName(goal.player_id) }}</td>
                      <td>{{ getAssists(goal.id) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Goalie Stats -->
              <h6 class="mt-4">Statistiky brankářů</h6>
              <div class="table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Tým</th>
                      <th>Brankář</th>
                      
                      <th>Zákroky</th>
                      <th>Zachráněné</th>
                      <th>Obdržené</th>
                      <th>Minuty</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="stats in matchGoalieStats" :key="stats.id">
                      <td>{{ getTeamName(stats.team_id) }}</td>
                      <td>{{ getPlayerName(stats.player_id) }}</td>
                      
                      <td>{{ stats.shots_against }}</td>
                      <td>{{ stats.saves }}</td>
                      <td>{{ stats.goals_against }}</td>
                      <td>{{ stats.minutes_played }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Goal Modal -->
    <div class="modal fade" id="addGoalModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Přidat gól</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="addGoal">
              <div class="mb-3">
                <label class="form-label">Střelec</label>
                <select class="form-select" v-model="newGoal.player_id" required>
                  <option v-for="player in availablePlayers" :key="player.id" :value="player.id">
                    {{ player.first_name }} {{ player.last_name }} ({{ getTeamName(player.team_id) }})
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Asistence</label>
                <select class="form-select" v-model="newGoal.assists" multiple>
                  <option v-for="player in availablePlayers" :key="player.id" :value="player.id">
                    {{ player.first_name }} {{ player.last_name }} ({{ getTeamName(player.team_id) }})
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Čas (sekundy)</label>
                <input type="number" class="form-control" v-model="newGoal.goal_time" required>
              </div>
              <div class="mb-3">
                <label class="form-label">Perioda</label>
                <select class="form-select" v-model="newGoal.period" required>
                  <option value="1">1.</option>
                  <option value="2">2.</option>
                  <option value="3">3.</option>
                  <option value="4">Prodloužení</option>
                </select>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" v-model="newGoal.is_power_play">
                  <label class="form-check-label">Power play</label>
                </div>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" v-model="newGoal.is_short_handed">
                  <label class="form-check-label">Short handed</label>
                </div>
              </div>
              <button type="submit" class="btn btn-primary">Přidat</button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Goalie Stats Modal -->
    <div class="modal fade" id="addGoalieStatsModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Přidat statistiky brankáře</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="addGoalieStats">
              <div class="mb-3">
                <label class="form-label">Brankář</label>
                <select class="form-select" v-model="newGoalieStats.player_id" required>
                  <option v-for="player in availableGoalies" :key="player.id" :value="player.id">
                    {{ player.first_name }} {{ player.last_name }} ({{ getTeamName(player.team_id) }})
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Zákroky</label>
                <input type="number" class="form-control" v-model="newGoalieStats.shots_against">
              </div>
              <div class="mb-3">
                <label class="form-label">Zachráněné</label>
                <input type="number" class="form-control" v-model="newGoalieStats.saves">
              </div>
              <div class="mb-3">
                <label class="form-label">Obdržené</label>
                <input type="number" class="form-control" v-model="newGoalieStats.goals_against">
              </div>
              <div class="mb-3">
                <label class="form-label">Minuty</label>
                <input type="number" class="form-control" v-model="newGoalieStats.minutes_played">
              </div>
              <button type="submit" class="btn btn-primary">Přidat</button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Match Record Modal -->
    <div class="modal fade" id="uploadRecordModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Vložit zápis z utkání</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="uploadMatchRecord">
              <div class="mb-3">
                <input type="file" class="form-control" ref="recordInput" accept="image/*" required />
              </div>
              <button type="submit" class="btn btn-primary">Nahrát zápis</button>
              <span v-if="uploadingRecord" class="ms-2">Nahrávám...</span>
              <span v-if="uploadRecordError" class="text-danger ms-2">{{ uploadRecordError }}</span>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pro zobrazení zápisu o utkání -->
    <div class="modal fade" id="recordModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Zápis o utkání</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body text-center">
            <template v-if="selectedRecordImage">
              <img :src="getLogoUrl(selectedRecordImage)" alt="Zápis o utkání" style="max-width:100%;border-radius:8px;box-shadow:0 2px 8px #0a1023;" />
            </template>
            <template v-else>
              <div class="alert alert-info mt-3">Zápis utkání nebyl nahrán.</div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Modal } from 'bootstrap';
import { sortLeagueTable } from '@/utils/leagueTableSort';

export default {
  name: 'ResultsPage',
  data() {
    return {
      selectedSeason: '',
      seasons: [],
      teams: [],
      matches: [],
      activeTab: 'table',
      leagueTable: [],
      playedMatches: [],
      scheduledMatches: [],
      scoringStats: [],
      goalieStats: [],
      selectedMatch: null,
      matchGoals: [],
      matchGoalieStats: [],
      availablePlayers: [],
      availableGoalies: [],
      newGoal: {
        player_id: '',
        team_id: '',
        goal_time: '',
        period: '1',
        is_power_play: false,
        is_short_handed: false,
        assists: []
      },
      newGoalieStats: {
        player_id: '',
        team_id: '',
        shots_against: '',
        saves: '',
        goals_against: '',
        minutes_played: ''
      },
      uploadingRecord: false,
      uploadRecordError: '',
      selectedRecordImage: '',
      loadingScoring: false,
      loadingTable: false,
      loadingPlayed: false,
      loadingGoalies: false,
      selectedScoringTeam: '',
      scoringTopLimit: 50,
      selectedGoaliesTeam: '',
      isPlayOff: false,
      regularMatches: [],
      playoffMatches: [],
      loadingPlayoff: false,
      playoffBracket: [],
    }
  },
  methods: {
    async loadSeasons() {
      try {
        const seasonsRes = await axios.get('/api/seasons');
        this.seasons = seasonsRes.data;
        if (this.seasons.length > 0) {
          this.selectedSeason = this.seasons[0].id;
          this.loadData();
        }
      } catch (error) {
        console.error('Error loading seasons:', error);
      }
    },
    async loadData() {
      if (!this.selectedSeason) return;
      this.loadingTable = true;
      this.loadingPlayed = true;
      try {
        const [teamsRes, matchesRes, regularMatchesRes] = await Promise.all([
          axios.get('/api/teams'),
          axios.get(`/api/matches?season_id=${this.selectedSeason}&isPlayOff=${this.isPlayOff}`),
          axios.get(`/api/matches?season_id=${this.selectedSeason}&isPlayOff=false`)
        ]);
        this.teams = teamsRes.data;
        this.matches = matchesRes.data;
        // Pro League table vždy použijeme pouze základní část
        this.regularMatches = regularMatchesRes.data;
        await this.calculateLeagueTable();
        this.filterMatches();
        this.loadingTable = false;
        this.loadingPlayed = false;
        // Load stats if those tabs are active
        if (this.activeTab === 'scoring') {
          await this.loadScoringStats();
        } else if (this.activeTab === 'goalies') {
          await this.loadGoalieStats();
        } else if (this.activeTab === 'playoff') {
          await this.loadPlayoffData();
        }
      } catch (error) {
        console.error('Error loading data:', error);
        this.loadingTable = false;
        this.loadingPlayed = false;
      }
    },
    async calculateLeagueTable() {
      const table = {};
      this.teams.forEach(team => {
        table[team.id] = {
          id: team.id,
          name: team.name,
          logo: team.logo,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          points: 0
        };
      });

      const ensureTeamInTable = (teamId) => {
        if (!table[teamId]) {
          table[teamId] = {
            id: teamId,
            name: 'Neznámý tým',
            logo: null,
            played: 0,
            won: 0,
            drawn: 0,
            lost: 0,
            goalsFor: 0,
            goalsAgainst: 0,
            points: 0
          };
        }
        return table[teamId];
      };

      // Calculate basic stats - použijeme pouze zápasy ze základní části
      const matchesToUse = this.regularMatches || this.matches.filter(m => !m.isPlayOff);
      matchesToUse.forEach(match => {
        const homeTeam = ensureTeamInTable(match.home_team_id);
        const awayTeam = ensureTeamInTable(match.away_team_id);
        if (match.status === 'played') {
          homeTeam.played++;
          awayTeam.played++;
          homeTeam.goalsFor += match.home_score;
          homeTeam.goalsAgainst += match.away_score;
          awayTeam.goalsFor += match.away_score;
          awayTeam.goalsAgainst += match.home_score;
          if (match.home_score > match.away_score) {
            homeTeam.won++;
            awayTeam.lost++;
            homeTeam.points += 3;
          } else if (match.home_score < match.away_score) {
            awayTeam.won++;
            homeTeam.lost++;
            awayTeam.points += 3;
          } else {
            homeTeam.drawn++;
            awayTeam.drawn++;
            homeTeam.points += 1;
            awayTeam.points += 1;
          }
        }
      });

      const tableArray = Object.values(table);
      this.leagueTable = sortLeagueTable(tableArray, matchesToUse);
    },
    filterMatches() {
      this.playedMatches = this.matches.filter(m => m.status === 'played');
      this.scheduledMatches = this.matches.filter(m => m.status === 'scheduled');
    },
    getTeamName(teamId) {
      const team = this.teams.find(t => t.id === teamId);
      return team ? team.name : 'Neznámý tým';
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('cs-CZ');
    },
    async loadScoringStats() {
      this.loadingScoring = true;
      try {
        const params = {};
        if (this.selectedSeason) params.season_id = this.selectedSeason;
        if (this.selectedScoringTeam) params.team_id = this.selectedScoringTeam;
        params.isPlayOff = this.isPlayOff;
        params.limit = this.scoringTopLimit;
        const response = await axios.get('/api/stats/scoring', { params });
        this.scoringStats = response.data;
      } catch (error) {
        console.error('Error loading scoring stats:', error);
      } finally {
        this.loadingScoring = false;
      }
    },
    async loadGoalieStats() {
      this.loadingGoalies = true;
      try {
        const params = {};
        if (this.selectedSeason) params.season_id = this.selectedSeason;
        if (this.selectedGoaliesTeam) params.team_id = this.selectedGoaliesTeam;
        params.isPlayOff = this.isPlayOff;
        const response = await axios.get('/api/stats/goalies', { params });
        this.goalieStats = response.data;
      } catch (error) {
        console.error('Error loading goalie stats:', error);
      } finally {
        this.loadingGoalies = false;
      }
    },
    async loadPlayoffData() {
      this.loadingPlayoff = true;
      try {
        const response = await axios.get('/api/playoff-bracket', {
          params: { season_id: this.selectedSeason }
        });
        this.playoffBracket = response.data;
      } catch (error) {
        console.error('Error loading playoff bracket:', error);
      } finally {
        this.loadingPlayoff = false;
      }
    },
    getUniquePlayoffRounds() {
      const rounds = [...new Set(this.playoffBracket.map(m => m.round))];
      return rounds.sort((a, b) => a - b);
    },
    getPlayoffBracketByRound(round) {
      return this.playoffBracket
        .filter(m => m.round === round)
        .sort((a, b) => a.match_number - b.match_number);
    },
    getPlayoffRoundLabel(round) {
      const labels = {
        1: 'Čtvrtfinále',
        2: 'Semifinále',
        3: 'O 3. místo',
        4: 'Finále'
      };
      return labels[round] || `Kolo ${round}`;
    },
    getPlayerName(playerId) {
      const player = this.availablePlayers.find(p => p.id === playerId);
      return player ? `${player.first_name} ${player.last_name}` : 'Neznámý hráč';
    },
    getPlayerTeamName(playerId) {
      const player = this.availablePlayers.find((p) => p.id === playerId);
      return player ? this.getTeamName(player.team_id) : 'Neznámý tým';
    },
    getAssists(goalId) {
      const goal = this.matchGoals.find((g) => g.id === goalId);
      if (!goal || goal.assists == null) return '';
      const raw = goal.assists;
      const ids = Array.isArray(raw) ? raw : raw != null && raw !== '' ? [raw] : [];
      return ids
        .map((id) => {
          const n = typeof id === 'object' && id != null && id.player_id != null ? id.player_id : id;
          return this.getPlayerName(n);
        })
        .filter(Boolean)
        .join(', ');
    },
    async showMatchDetails(match) {
      this.selectedMatch = match;
      try {
        const [goalsRes, goalieStatsRes] = await Promise.all([
          axios.get(`/api/matches/${match.id}/goals`),
          axios.get(`/api/matches/${match.id}/goalie-stats`)
        ]);
        this.matchGoals = goalsRes.data;
        this.matchGoalieStats = goalieStatsRes.data;
        
        const [homePlayersRes, awayPlayersRes] = await Promise.all([
          axios.get(`/api/teams/${match.home_team_id}/players`),
          axios.get(`/api/teams/${match.away_team_id}/players`)
        ]);
        this.availablePlayers = [...homePlayersRes.data, ...awayPlayersRes.data];
        this.availableGoalies = this.availablePlayers.filter(p => p.position === 'Brankář');
        
        const modal = new Modal(document.getElementById('matchDetailsModal'));
        modal.show();
      } catch (error) {
        console.error('Error loading match details:', error);
      }
    },
    showAddGoalForm() {
      this.newGoal = {
        player_id: '',
        team_id: '',
        goal_time: '',
        period: '1',
        is_power_play: false,
        is_short_handed: false,
        assists: []
      };
      const modal = new Modal(document.getElementById('addGoalModal'));
      modal.show();
    },
    showAddGoalieStatsForm() {
      this.newGoalieStats = {
        player_id: '',
        team_id: '',
        shots_against: '',
        saves: '',
        goals_against: '',
        minutes_played: ''
      };
      const modal = new Modal(document.getElementById('addGoalieStatsModal'));
      modal.show();
    },
    async addGoal() {
      try {
        const player = this.availablePlayers.find(p => p.id === this.newGoal.player_id);
        this.newGoal.team_id = player.team_id;
        
        await axios.post(`/api/admin/matches/${this.selectedMatch.id}/goals`, this.newGoal, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        await this.showMatchDetails(this.selectedMatch);
        
        const modal = Modal.getInstance(document.getElementById('addGoalModal'));
        modal.hide();
      } catch (error) {
        console.error('Error adding goal:', error);
      }
    },
    async addGoalieStats() {
      try {
        const player = this.availableGoalies.find(p => p.id === this.newGoalieStats.player_id);
        this.newGoalieStats.team_id = player.team_id;
        
        await axios.post(`/api/admin/matches/${this.selectedMatch.id}/goalie-stats`, this.newGoalieStats, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        await this.showMatchDetails(this.selectedMatch);
        
        const modal = Modal.getInstance(document.getElementById('addGoalieStatsModal'));
        modal.hide();
      } catch (error) {
        console.error('Error adding goalie stats:', error);
      }
    },
    getLogoUrl(logo) {
      if (!logo) return '';
      // If logo is already a full URL, return it
      if (logo.startsWith('http')) return logo;
      
      // Handle match records: if it's a full path (old format), use it as-is
      if (logo.startsWith('/uploads/match_records/')) {
        return `${process.env.VUE_APP_API_URL}${logo}`;
      }
      
      // Handle match records: if it's just a filename (new format), construct the path
      if (!logo.startsWith('/') && logo.includes('.')) {
        return `${process.env.VUE_APP_API_URL}/uploads/match_records/${logo}`;
      }
      
      // For team logos and other paths, prepend the API URL
      return `${process.env.VUE_APP_API_URL}${logo}`;
    },
    getTeamLogo(teamId) {
      const team = this.teams.find(t => t.id === teamId);
      return team ? team.logo : null;
    },
    showUploadRecordModal() {
      this.uploadRecordError = '';
      this.uploadingRecord = false;
      this.$refs.recordInput && (this.$refs.recordInput.value = '');
      const modal = new Modal(document.getElementById('uploadRecordModal'));
      modal.show();
    },
    async uploadMatchRecord() {
      if (!this.selectedMatch) return;
      const fileInput = this.$refs.recordInput;
      if (!fileInput || !fileInput.files.length) return;
      const file = fileInput.files[0];
      const formData = new FormData();
      formData.append('record', file);
      this.uploadingRecord = true;
      this.uploadRecordError = '';
      try {
        await axios.post(`/api/admin/matches/${this.selectedMatch.id}/record`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        this.uploadingRecord = false;
        const modal = Modal.getInstance(document.getElementById('uploadRecordModal'));
        modal.hide();
        await this.showMatchDetails(this.selectedMatch);
      } catch (err) {
        this.uploadingRecord = false;
        this.uploadRecordError = err.response?.data?.error || 'Chyba při nahrávání zápisu.';
      }
    },
    showRecordModal(match) {
      this.selectedRecordImage = match.record_image || '';
      const modal = new Modal(document.getElementById('recordModal'));
      modal.show();
    },
  },
  mounted() {
    this.loadSeasons();
  },
  watch: {
    activeTab() {
      if (this.activeTab === 'scoring') {
        this.loadScoringStats();
      } else if (this.activeTab === 'goalies') {
        this.loadGoalieStats();
      } else if (this.activeTab === 'playoff') {
        this.loadPlayoffData();
      }
    }
  }
}
</script>

<style scoped>
.results-page {
  max-width: 1200px;
  margin: 0 auto;
  background: #0a1023;
  color: #e0e6f7;
  min-height: 100vh;
  padding: 2rem 1rem 3rem;
}

.section-title {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
}

.table {
  background: transparent;
  color: #e0e6f7;
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
  border-collapse: separate;
  border-spacing: 0;
}

.table thead th {
  background: rgba(31, 224, 255, 0.06);
  color: #1fe0ff;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: none;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(31, 224, 255, 0.1);
}

.table-striped tbody tr:nth-of-type(odd) {
  background: rgba(31, 224, 255, 0.02);
}

.table-striped tbody tr:nth-of-type(even) {
  background: transparent;
}

.table td, .table th {
  border: none;
  padding: 0.7rem 1rem;
  vertical-align: middle;
}

.table td {
  border-bottom: 1px solid rgba(31, 224, 255, 0.04);
}

.table tbody tr {
  transition: background 0.2s ease;
}

.table tbody tr:hover {
  background: rgba(31, 224, 255, 0.03);
}

.nav-tabs {
  border-bottom: none;
  gap: 0.5rem;
  padding-bottom: 1rem;
  flex-wrap: wrap;
}

.nav-tabs .nav-link {
  color: #8892b0;
  background: transparent;
  border: 1px solid rgba(31, 224, 255, 0.13);
  border-radius: 999px;
  padding: 0.45rem 1.15rem;
  font-weight: 600;
  font-size: 0.88rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-right: 0;
  white-space: nowrap;
}

.nav-tabs .nav-link:hover {
  color: #1fe0ff;
  border-color: rgba(31, 224, 255, 0.3);
  background: rgba(31, 224, 255, 0.05);
}

.nav-tabs .nav-link.active {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  color: #0a1023 !important;
  font-weight: 700;
  border: none;
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.25);
}

.form-select {
  background: #0a1023;
  color: #e0e6f7;
  border: 1px solid rgba(31, 224, 255, 0.13);
  border-radius: 0.5rem;
  padding: 0.6rem 0.85rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 1rem;
}

.form-select:focus {
  border-color: #1fe0ff;
  box-shadow: 0 0 0 3px rgba(31, 224, 255, 0.15);
  background: #0a1023;
  color: #e0e6f7;
}

.form-control {
  background: #0a1023;
  color: #e0e6f7;
  border: 1px solid rgba(31, 224, 255, 0.13);
  border-radius: 0.5rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-control:focus {
  background: #0a1023;
  color: #e0e6f7;
  border-color: #1fe0ff;
  box-shadow: 0 0 0 3px rgba(31, 224, 255, 0.15);
}

.form-label {
  color: #8892b0;
  font-weight: 500;
  font-size: 0.9rem;
}

.form-check-label {
  color: #e0e6f7;
}

.form-check-input {
  background-color: #0a1023;
  border-color: rgba(31, 224, 255, 0.2);
}

.form-check-input:checked {
  background-color: #1fe0ff;
  border-color: #1fe0ff;
}

.modal-content {
  background: #151d3a;
  color: #e0e6f7;
  border: 1px solid rgba(31, 224, 255, 0.1);
  border-radius: 1rem;
  box-shadow: 0 16px 48px rgba(10, 16, 35, 0.7);
}

.modal-header {
  border-bottom: 1px solid rgba(31, 224, 255, 0.08);
  padding: 1.25rem 1.5rem;
}

.modal-footer {
  border-top: 1px solid rgba(31, 224, 255, 0.08);
  padding: 1rem 1.5rem;
}

.btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
  opacity: 0.5;
}

.btn-close:hover {
  opacity: 0.9;
}

.match-detail-meta {
  color: #8892b0;
}

.played-match-actions {
  vertical-align: middle;
  min-width: 12rem;
}

.played-action-btn {
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.played-action-btn--stats {
  background: linear-gradient(135deg, #1fe0ff 0%, #0a7cff 100%);
  color: #0a1023;
  border: none;
  box-shadow: 0 2px 8px rgba(31, 224, 255, 0.2);
}

.played-action-btn--stats:hover {
  filter: brightness(1.08);
  color: #0a1023;
  box-shadow: 0 4px 14px rgba(31, 224, 255, 0.35);
}

.played-action-btn--record {
  background: transparent;
  color: #c8d0eb;
  border: 1px solid rgba(31, 224, 255, 0.25);
}

.played-action-btn--record:hover {
  background: rgba(31, 224, 255, 0.08);
  color: #1fe0ff;
  border-color: rgba(31, 224, 255, 0.45);
}

.team-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.team-logo-placeholder {
  width: 28px;
  height: 28px;
  background: rgba(136, 146, 176, 0.15);
  border-radius: 6px;
}

.btn-info.btn-sm, .btn-info {
  background: linear-gradient(135deg, #1fe0ff 0%, #0a7cff 100%);
  color: #0a1023;
  font-weight: 700;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.5rem;
  font-size: 0.95rem;
  box-shadow: 0 2px 8px rgba(31, 224, 255, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-info.btn-sm:hover, .btn-info:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.35);
}

.btn-primary {
  background: linear-gradient(135deg, #1fe0ff 0%, #0a7cff 100%);
  color: #0a1023;
  font-weight: 700;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.5rem;
  box-shadow: 0 2px 8px rgba(31, 224, 255, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.35);
  color: #0a1023;
}

.btn-secondary {
  background: rgba(136, 146, 176, 0.1);
  color: #8892b0;
  border: 1px solid rgba(136, 146, 176, 0.2);
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-secondary:hover {
  background: rgba(136, 146, 176, 0.2);
  color: #e0e6f7;
}

.btn-sm {
  padding: 0.3rem 0.85rem;
  font-size: 0.82rem;
  border-radius: 0.4rem;
}

.alert-info {
  background: rgba(31, 224, 255, 0.08);
  color: #1fe0ff;
  border: 1px solid rgba(31, 224, 255, 0.15);
  border-radius: 0.75rem;
}

.playoff-bracket {
  padding: 2rem 0;
}

.playoff-round {
  margin-bottom: 3rem;
}

.playoff-round h5 {
  color: #1fe0ff;
  font-weight: 700;
}

.playoff-matches {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
}

.playoff-match {
  min-width: 250px;
}

.match-card {
  background: #151d3a;
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(31, 224, 255, 0.08);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.match-card:hover {
  border-color: rgba(31, 224, 255, 0.25);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4);
}

.team-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(31, 224, 255, 0.06);
}

.team-row:last-child {
  border-bottom: none;
}

.team-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.team-logo-small {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.score {
  font-weight: 700;
  font-size: 1.2rem;
  color: #1fe0ff;
  min-width: 30px;
  text-align: right;
}

.match-date {
  text-align: center;
  color: #8892b0;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(31, 224, 255, 0.06);
}

.match-winner {
  text-align: center;
  color: #1fe0ff;
  font-weight: 700;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(31, 224, 255, 0.06);
}

.winner-badge {
  color: #1fe0ff;
  font-weight: 700;
  font-size: 1.2rem;
}

.team-logo-placeholder-small {
  width: 24px;
  height: 24px;
  background: rgba(136, 146, 176, 0.15);
  border-radius: 6px;
  display: inline-block;
  margin-right: 0.5rem;
}

.scoring-top-limit-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem 0.75rem;
  flex-wrap: wrap;
}

.scoring-top-limit-caption {
  font-size: 0.8rem;
  font-weight: 500;
  color: #8892b0;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.scoring-top-limit-group {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.scoring-top-limit-group .scoring-top-limit-btn {
  font-size: 0.8rem;
  padding: 0.35rem 0.7rem;
  line-height: 1.2;
  border-radius: 0.45rem;
  background: transparent;
  color: #8892b0;
  border: 1px solid rgba(136, 146, 176, 0.35);
  font-weight: 500;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.scoring-top-limit-group .btn-check:checked + .scoring-top-limit-btn {
  background: rgba(31, 224, 255, 0.1);
  color: #a8e8f5;
  border-color: rgba(31, 224, 255, 0.28);
  box-shadow: none;
  font-weight: 600;
}

.scoring-top-limit-group .scoring-top-limit-btn:hover {
  background: rgba(31, 224, 255, 0.06);
  color: #c5eef7;
  border-color: rgba(31, 224, 255, 0.22);
}

.scoring-top-limit-group .btn-check:focus-visible + .scoring-top-limit-btn {
  box-shadow: 0 0 0 2px rgba(31, 224, 255, 0.25);
}

.btn-group .btn-check:checked + .btn-outline-primary {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  color: #0a1023;
  font-weight: 700;
  border-color: transparent;
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.25);
}

.btn-group .btn-outline-primary {
  background: transparent;
  color: #8892b0;
  border: 1px solid rgba(31, 224, 255, 0.13);
  border-radius: 999px;
  font-weight: 600;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-group .btn-outline-primary:hover {
  background: rgba(31, 224, 255, 0.05);
  color: #1fe0ff;
  border-color: rgba(31, 224, 255, 0.3);
}

.btn-group .btn-outline-primary:not(:first-child) {
  margin-left: 0.5rem;
}

@media (max-width: 768px) {
  .results-page {
    padding: 1rem 0.5rem 2rem;
  }

  .nav-tabs {
    overflow-x: auto;
    flex-wrap: nowrap;
    scrollbar-width: none;
  }

  .nav-tabs::-webkit-scrollbar {
    display: none;
  }
}
</style> 